import { useEffect, useMemo, useState } from "react";
import { Plus, Loader2, Search, Package, Trash2, Wrench, Lock } from "lucide-react";
import api, { money } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

let _pseq = 0;
const emptyPart = () => ({ _key: `p_${Date.now()}_${_pseq++}`, name: "", price: 0, source: "" });

export default function Catalog() {
  const [builtin, setBuiltin] = useState([]);
  const [custom, setCustom] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", system: "", hours: 1, parts: [emptyPart()] });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/catalog");
      setBuiltin(data.builtin || []);
      setCustom(data.custom || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updatePart = (i, k, v) => setForm((f) => ({ ...f, parts: f.parts.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)) }));
  const addPart = () => setForm((f) => ({ ...f, parts: [...f.parts, emptyPart()] }));
  const removePart = (i) => setForm((f) => ({ ...f, parts: f.parts.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Job name is required");
    setSaving(true);
    try {
      await api.post("/catalog", {
        name: form.name, system: form.system, hours: Number(form.hours || 0),
        parts: form.parts.filter((p) => p.name.trim()).map((p) => ({ name: p.name, price: Number(p.price || 0), source: p.source })),
      });
      toast.success("Catalog job saved");
      setOpen(false);
      setForm({ name: "", system: "", hours: 1, parts: [emptyPart()] });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const removeJob = async (id) => {
    try { await api.delete(`/catalog/${id}`); setCustom((x) => x.filter((j) => j.id !== id)); toast.success("Deleted"); }
    catch { toast.error("Failed to delete"); }
  };

  const all = useMemo(() => [...custom.map((j) => ({ ...j, builtin: false })), ...builtin.map((j) => ({ ...j, builtin: true }))], [custom, builtin]);
  const filtered = all.filter((j) => !q || (j.name || "").toLowerCase().includes(q.toLowerCase()) || (j.system || "").toLowerCase().includes(q.toLowerCase()));
  const partsTotal = (j) => (j.parts || []).reduce((s, p) => s + Number(p.price || 0), 0);

  return (
    <Layout
      title="Parts & Service Catalog"
      action={<Button data-testid="new-catalog-button" onClick={() => setOpen(true)} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"><Plus className="h-4 w-4 mr-1.5" /> Add Job / Part</Button>}
    >
      <div className="space-y-4 animate-fade-up">
        <div className="flex items-center gap-3">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input data-testid="catalog-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search jobs or systems" className="pl-9 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50" />
          </div>
          <span className="text-xs text-slate-500">{custom.length} custom · {builtin.length} built-in</span>
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((j) => (
              <div key={j.id} data-testid={`catalog-card-${j.id}`} className="rounded-xl border border-slate-800 bg-slate-900 p-5 transition-transform duration-200 hover:-translate-y-[2px]">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-100 truncate">{j.name}</p>
                    <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5"><Wrench className="h-3 w-3" /> {j.system || "General"} · {j.hours}h labor</p>
                  </div>
                  {j.builtin ? (
                    <span className="text-slate-600" title="Built-in"><Lock className="h-4 w-4" /></span>
                  ) : (
                    <Button variant="ghost" size="icon" data-testid={`delete-catalog-${j.id}`} onClick={() => removeJob(j.id)} className="h-8 w-8 text-slate-500 hover:text-rose-400 hover:bg-slate-800"><Trash2 className="h-4 w-4" /></Button>
                  )}
                </div>
                <div className="mt-3 space-y-1.5">
                  {(j.parts || []).map((p, i) => (
                    <div key={`${p.name || "part"}-${i}`} className="flex items-center gap-2 text-sm">
                      <Package className="h-3.5 w-3.5 text-slate-600 shrink-0" />
                      <span className="text-slate-300 truncate flex-1">{p.name}</span>
                      {p.source && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 shrink-0">{p.source}</span>}
                      <span className="text-slate-200 tabular-nums font-medium shrink-0">{money(p.price)}</span>
                    </div>
                  ))}
                  {(j.parts || []).length === 0 && <p className="text-xs text-slate-600">No parts listed</p>}
                </div>
                {(j.parts || []).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-800 flex justify-between text-xs">
                    <span className="text-slate-500">Parts subtotal</span>
                    <span className="text-amber-400 font-semibold tabular-nums">{money(partsTotal(j))}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-100 font-head">Add Catalog Job</DialogTitle>
            <DialogDescription className="text-slate-500">Define a repair with its labor hours, parts, costs and sources.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2"><Label className="text-slate-300 text-xs">Job name *</Label><Input data-testid="catalog-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="h-10 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50" /></div>
              <div className="space-y-1.5"><Label className="text-slate-300 text-xs">System</Label><Input value={form.system} onChange={(e) => setForm((f) => ({ ...f, system: e.target.value }))} placeholder="e.g. Brakes" className="h-10 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50" /></div>
              <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Labor hours</Label><Input data-testid="catalog-hours-input" type="number" step="0.1" value={form.hours} onChange={(e) => setForm((f) => ({ ...f, hours: e.target.value }))} className="h-10 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50" /></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-slate-300 text-xs">Parts (cost & source)</Label>
                <Button size="sm" variant="ghost" data-testid="catalog-add-part" onClick={addPart} className="h-7 text-amber-400 hover:text-amber-300"><Plus className="h-3.5 w-3.5 mr-1" /> Part</Button>
              </div>
              <div className="space-y-2">
                {form.parts.map((p, i) => (
                  <div key={p._key} className="grid grid-cols-12 gap-2">
                    <Input data-testid={`catalog-part-name-${i}`} value={p.name} onChange={(e) => updatePart(i, "name", e.target.value)} placeholder="Part name" className="col-span-5 h-9 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50" />
                    <Input value={p.source} onChange={(e) => updatePart(i, "source", e.target.value)} placeholder="Source" className="col-span-4 h-9 bg-slate-950 border-slate-700 focus-visible:ring-amber-500/50" />
                    <Input data-testid={`catalog-part-price-${i}`} type="number" step="0.01" value={p.price} onChange={(e) => updatePart(i, "price", e.target.value)} placeholder="$" className="col-span-2 h-9 bg-slate-950 border-slate-700 text-right focus-visible:ring-amber-500/50" />
                    <Button variant="ghost" size="icon" onClick={() => removePart(i)} className="col-span-1 h-8 w-8 text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700">Cancel</Button>
            <Button data-testid="save-catalog-button" onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">{saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
